import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import type { CategoryRef, TagRef } from '@news-cms/shared';
import { Input } from '@/components/ui/input';
import {
  categoriesListQueryOptions,
  tagsListQueryOptions,
  useCreateCategory,
  useCreateTag,
} from '@/lib/taxonomies';

type Kind = 'category' | 'tag';

interface TaxonomyPickerProps {
  kind: Kind;
  label: string;
  selected: CategoryRef[] | TagRef[];
  onChange: (next: CategoryRef[] | TagRef[]) => void;
  canCreate: boolean;
  placeholder?: string;
}

export function TaxonomyPicker({
  kind,
  label,
  selected,
  onChange,
  canCreate,
  placeholder,
}: TaxonomyPickerProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(search, 200);

  const categoryQuery = useQuery({
    ...categoriesListQueryOptions(debouncedSearch || undefined),
    enabled: kind === 'category',
  });
  const tagQuery = useQuery({
    ...tagsListQueryOptions(debouncedSearch || undefined),
    // The list endpoint now returns { items, pagination }; the picker only
    // needs the flat array (search narrows it, so page 1 is enough).
    select: (d) => d.items,
    enabled: kind === 'tag',
  });
  const query: { data?: (CategoryRef | TagRef)[]; isLoading: boolean } =
    kind === 'category' ? categoryQuery : tagQuery;
  const createCategory = useCreateCategory();
  const createTag = useCreateTag();

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const available = (query.data ?? []).filter((item) => !selectedIds.has(item.id));
  const exactMatch = (query.data ?? []).find(
    (item) => item.name.toLowerCase() === search.trim().toLowerCase(),
  );
  const showCreate = canCreate && search.trim().length > 0 && !exactMatch && !query.isLoading;

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const addSelected = (item: CategoryRef | TagRef) => {
    onChange([...selected, item] as CategoryRef[] | TagRef[]);
    setSearch('');
    setOpen(false);
  };

  const removeSelected = (id: string) => {
    onChange(selected.filter((s) => s.id !== id));
  };

  const handleCreate = async () => {
    const name = search.trim();
    if (!name) return;
    if (kind === 'category') {
      const created = await createCategory.mutateAsync({ name });
      addSelected(created);
    } else {
      const created = await createTag.mutateAsync({ name });
      addSelected(created);
    }
  };

  const isPending = createCategory.isPending || createTag.isPending;

  return (
    <div ref={containerRef} className="relative space-y-1.5">
      <div className="text-sm font-medium">{label}</div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((item) => (
            <span
              key={item.id}
              className="bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-300 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs"
            >
              {item.name}
              <button
                type="button"
                onClick={() => removeSelected(item.id)}
                aria-label={`ลบ ${item.name}`}
                className="hover:text-brand-950 dark:hover:text-brand-100"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Input
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={
          placeholder ?? (kind === 'category' ? 'ค้นหา / สร้างหมวด' : 'ค้นหา / สร้างแท็ก')
        }
        disabled={isPending}
      />

      {open && (search.length > 0 || available.length > 0) && (
        <div className="bg-popover absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 shadow-lg dark:border-slate-800">
          {query.isLoading && <div className="px-3 py-2 text-xs text-slate-500">กำลังโหลด...</div>}
          {!query.isLoading && available.length === 0 && !showCreate && (
            <div className="px-3 py-2 text-xs text-slate-500">ไม่พบ</div>
          )}
          {available.slice(0, 20).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => addSelected(item)}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {item.name}
              <span className="ml-2 font-mono text-[10px] text-slate-400">/{item.slug}</span>
            </button>
          ))}
          {showCreate && (
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={isPending}
              className="text-brand-700 dark:text-brand-300 flex w-full items-center gap-2 border-t border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800"
            >
              <Plus className="h-3.5 w-3.5" />
              {isPending ? `กำลังสร้าง "${search.trim()}"...` : `สร้าง "${search.trim()}"`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function useDebounce<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
